/**
 * ZoomModal component - Modal for selecting zoom modes
 * Factory function following the app's architectural patterns
 */

import { createDropdown, type DropdownAPI } from "@UI/Dropdown/index.js";
import { getModalManager } from "@UI/ModalManager/index.js";
import type { ZoomMode } from "@/types/user-types.js";

export interface ZoomOption {
  readonly mode: ZoomMode;
  readonly icon: string;
  readonly label: string;
  readonly description: string;
}

export interface ZoomModalConfig {
  readonly anchorElement: HTMLElement;
  readonly currentMode: ZoomMode;
  readonly hasSelection: boolean;
  readonly onSelect: (mode: ZoomMode) => void;
  readonly onClose?: () => void;
}

export interface ZoomModalAPI {
  readonly show: () => void;
  readonly destroy: () => void;
}

/**
 * Creates ZoomModal component with zoom mode selection
 * @param config - Configuration for the zoom modal
 * @returns ZoomModal API
 */
export const createZoomModal = (config: ZoomModalConfig): ZoomModalAPI => {
  const { anchorElement, currentMode, hasSelection, onSelect, onClose } =
    config;

  let isDestroyed = false;
  let dropdownAPI: DropdownAPI | null = null;

  // Define zoom options
  const getZoomOptions = (): ZoomOption[] => {
    console.log(
      "🎯 ZoomModal: Getting zoom options with hasSelection:",
      hasSelection
    );

    const baseOptions: ZoomOption[] = [
      {
        mode: "zoom_width",
        icon: "zoom_width",
        label: "Fit Width",
        description: "Fit the content width to the viewport",
      },
      {
        mode: "zoom_height",
        icon: "zoom_height",
        label: "Fit Height",
        description: "Fit the content height to the viewport",
      },
      {
        mode: "zoom_all",
        icon: "zoom_all",
        label: "Fit All",
        description: "Fit all content to the viewport",
      },
      {
        mode: "zoom_1_1",
        icon: "zoom_1_1",
        label: "Actual Size",
        description: "Display at 100% scale",
      },
    ];

    // Add zoom_element option only if there's a selection
    if (hasSelection) {
      console.log("🎯 ZoomModal: Adding zoom_element option at index 3");
      baseOptions.splice(3, 0, {
        mode: "zoom_element",
        icon: "zoom_element",
        label: "Fit Selection",
        description: "Fit selected element to the viewport",
      });
    }

    console.log(
      "🎯 ZoomModal: Final options:",
      baseOptions.map((opt) => opt.mode)
    );
    return baseOptions;
  };

  /**
   * Create the zoom dropdown with options
   */
  const createZoomDropdown = (): DropdownAPI => {
    const options = getZoomOptions();

    // Convert zoom options to dropdown data format
    // If currentMode is "zoom_element" but there is no selection,
    // that option is not shown. In that case, preselect a sensible fallback
    // ("zoom_all") so the list has a highlighted item.
    const selectedModeForList: ZoomMode =
      !hasSelection && (currentMode as string) === "zoom_element"
        ? ("zoom_all" as ZoomMode)
        : currentMode;

    const dropdownData = options.map((option) => ({
      id: option.mode,
      label: option.label,
      icon: option.icon,
      selected: option.mode === selectedModeForList,
    }));

    console.log("🎯 ZoomModal: Creating dropdown with data:", dropdownData);

    return createDropdown({
      data: dropdownData,
      multiSelect: false, // Single selection
      anchorElement: anchorElement,
      maxHeight: "300px",
      onSelectionChange: (selectedIds) => {
        console.log("🎯 ZoomModal: Dropdown selection changed:", selectedIds);

        if (selectedIds.length > 0) {
          const selectedMode = selectedIds[0] as ZoomMode;
          onSelect(selectedMode);
        }
      },
      onClose: () => {
        console.log("🎯 ZoomModal: Dropdown closed");
        onClose?.();
      },
    });
  };

  /**
   * Initialize the zoom dropdown (without modal)
   */
  const initZoomModal = (): void => {
    try {
      console.log("🎯 ZoomModal: Initializing with config:", {
        currentMode,
        hasSelection,
        anchorElementExists: !!anchorElement,
      });

      dropdownAPI = createZoomDropdown();

      console.log("🎯 ZoomModal: Dropdown created successfully");
    } catch (error) {
      console.error(
        "🎯 ZoomModal: Failed to create ZoomModal component:",
        error
      );
      throw error;
    }
  };

  /**
   * Show the modal using ModalManager
   */
  const show = (): void => {
    console.log("🎯 ZoomModal: show() called", {
      isDestroyed,
      hasDropdownAPI: !!dropdownAPI,
    });

    if (isDestroyed || !dropdownAPI) {
      console.warn("🎯 ZoomModal: Cannot show - destroyed or no dropdownAPI");
      return;
    }

    // Get anchor position
    const anchorRect = anchorElement.getBoundingClientRect();

    console.log("🎯 ZoomModal: Showing modal with ModalManager at position:", {
      top: anchorRect.top,
      left: anchorRect.left,
    });

    // Get the modal manager and show the dropdown
    const modalManager = getModalManager();

    modalManager.show({
      content: dropdownAPI.getElement(),
      anchor: {
        top: anchorRect.top,
        left: anchorRect.left,
      },
      offset: { x: 0, y: 40 },
      closeOnBackdrop: true,
      showBackdrop: false,
      onClose: () => {
        console.log("🎯 ZoomModal: ModalManager closed");
        onClose?.();
      },
    });

    console.log("🎯 ZoomModal: ModalManager.show() completed");
  };

  /**
   * Clean up resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    dropdownAPI?.destroy();
    dropdownAPI = null;
  };

  // Initialize the component
  initZoomModal();

  return {
    show,
    destroy,
  };
};
